import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { css } from '@emotion/react';
import Header from '@/components/ui/Header';

// --- Safe triangle hover utility ---

function pointInTriangle(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
) {
  const d1 = (px - bx) * (ay - by) - (ax - bx) * (py - by);
  const d2 = (px - cx) * (by - cy) - (bx - cx) * (py - cy);
  const d3 = (px - ax) * (cy - ay) - (cx - ax) * (py - ay);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

function useSubmenuSafeTriangle() {
  const mousePos = useRef({ x: 0, y: 0 });
  const anchorPos = useRef({ x: 0, y: 0 });
  const submenuRef = useRef<HTMLDivElement | null>(null);
  const pendingItem = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeItem = useRef<string | null>(null);

  const trackMouse = useCallback((e: React.MouseEvent) => {
    mousePos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const isInSafeTriangle = useCallback(() => {
    const sub = submenuRef.current;
    if (!sub || !activeItem.current) return false;
    const rect = sub.getBoundingClientRect();
    const { x, y } = mousePos.current;
    const { x: ax, y: ay } = anchorPos.current;
    // Triangle: anchor point → top-right of submenu → bottom-right of submenu
    // Triangle: anchor point → top-left of submenu → bottom-left of submenu
    return pointInTriangle(x, y, ax, ay, rect.left, rect.top, rect.left, rect.bottom);
  }, []);

  const requestHover = useCallback(
    (item: string, setHovered: (item: string | null) => void) => {
      if (item === activeItem.current) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      if (isInSafeTriangle()) {
        pendingItem.current = item;
        timeoutRef.current = setTimeout(() => {
          activeItem.current = item;
          anchorPos.current = { ...mousePos.current };
          setHovered(item);
          pendingItem.current = null;
        }, 100);
      } else {
        activeItem.current = item;
        anchorPos.current = { ...mousePos.current };
        setHovered(item);
        pendingItem.current = null;
      }
    },
    [isInSafeTriangle],
  );

  const setActive = useCallback((item: string | null) => {
    activeItem.current = item;
    if (!item) anchorPos.current = { x: 0, y: 0 };
  }, []);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pendingItem.current = null;
  }, []);

  return { trackMouse, requestHover, submenuRef, setActive, cleanup };
}

// --- Icons ---

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11 11L14.5 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
      <path
        d="M1 1L5 5L9 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
      <path
        d="M6 1V13M6 13L1 8M6 13L11 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowUpIcon() {
  return (
    <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
      <path
        d="M6 13V1M6 1L1 6M6 1L11 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
      <path d="M1 1L8 8M8 1L1 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function EllipsisIcon() {
  return (
    <svg width="15" height="3" viewBox="0 0 15 3" fill="currentColor">
      <circle cx="1.5" cy="1.5" r="1.5" />
      <circle cx="7.5" cy="1.5" r="1.5" />
      <circle cx="13.5" cy="1.5" r="1.5" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
      <path
        d="M1 1L5 5L1 9"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
      <path
        d="M5 9L1 5L5 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// --- Data ---

type AppliedFilter = {
  id: string;
  category: string;
  condition?: string;
  values: string[];
};

type Subscriber = {
  email: string;
  name: string;
  status: string;
  lastActivity: string;
  totalSales: string;
  segments: { label: string; color: string }[];

  emailsOpened: number;
  openRate: number;
  emailsClicked: number;
  clickRate: number;
  signUpSource: string;
  signUpDate: string;
  dataFields: { website?: string; phoneNumber?: string; company?: string; birthday?: string };
};

const SEGMENT_COLORS: Record<string, string> = {
  'Profile social form': '#e8c0fd',
  'May promotion': '#def2ea',
  'Spring sale': '#efc5c7',
  'Summer campaign': '#cfddd7',
  'VIP customers': '#c8e6c9',
  'Welcome series': '#fff98a',
  'Weekly digest': '#9ebbed',
  'Holiday sale': '#ffdddb',
  'Product launch': '#f5f5e4',
  'Loyalty rewards': '#d4c5f9',
  'Refer a friend': '#ffd6a5',
  'Birthday club': '#f9c0c0',
  'Webinar attendees': '#b5d8f7',
  'Free trial': '#c5e8d0',
  'Cart recovery': '#f7d6b0',
  'Win-back': '#d5d5f5',
  'Early access': '#f0e68c',
  'Beta testers': '#c0d8e8',
  'Instagram followers': '#e8d0f0',
  'Black Friday': '#b0c4de',
};

function seg(name: string): { label: string; color: string } {
  return { label: name, color: SEGMENT_COLORS[name] || '#e7e5e4' };
}

const subscribers: Subscriber[] = [
  {
    email: 'jane@austen.com',
    name: 'Jane Austen',
    status: 'Active',
    lastActivity: 'Mar 18, 2026',
    totalSales: '$102.50',
    segments: [
      seg('Profile social form'),
      seg('May promotion'),
      seg('Welcome series'),
      seg('Weekly digest'),
      seg('VIP customers'),
      seg('Holiday sale'),
    ],
    emailsOpened: 45,
    openRate: 72,
    emailsClicked: 18,
    clickRate: 29,
    signUpSource: 'Form opt-in',
    signUpDate: 'Jan 10, 2025',
    dataFields: { website: 'janeausten.com', company: 'Austen & Co' },
  },
  {
    email: 'mark.twain@example.com',
    name: 'Mark Twain',
    status: 'Active',
    lastActivity: 'Mar 15, 2026',
    totalSales: '$76.30',
    segments: [seg('Profile social form'), seg('May promotion')],
    emailsOpened: 30,
    openRate: 55,
    emailsClicked: 10,
    clickRate: 18,
    signUpSource: 'Form opt-in',
    signUpDate: 'Feb 22, 2025',
    dataFields: { phoneNumber: '555-0102' },
  },
  {
    email: 'charles.dickens@literature.net',
    name: 'Charles Dickens',
    status: 'Unconfirmed',
    lastActivity: 'Resend opt-in',
    totalSales: '$89.90',
    segments: [seg('May promotion'), seg('Spring sale'), seg('Free trial')],
    emailsOpened: 0,
    openRate: 0,
    emailsClicked: 0,
    clickRate: 0,
    signUpSource: 'CSV import',
    signUpDate: 'Mar 01, 2026',
    dataFields: { company: 'Dickens Publishing' },
  },
  {
    email: 'virginia.woolf@mail.com',
    name: 'Virginia Woolf',
    status: 'Active',
    lastActivity: 'Mar 12, 2026',
    totalSales: '$115.70',
    segments: [
      seg('Profile social form'),
      seg('Summer campaign'),
      seg('Loyalty rewards'),
      seg('Birthday club'),
      seg('Webinar attendees'),
    ],
    emailsOpened: 52,
    openRate: 81,
    emailsClicked: 25,
    clickRate: 39,
    signUpSource: 'Form opt-in',
    signUpDate: 'Nov 05, 2024',
    dataFields: { website: 'virginiawoolf.co', birthday: 'Jan 25' },
  },
  {
    email: 'mark.twain@novelist.com',
    name: 'Mark Twain',
    status: 'Bounced',
    lastActivity: 'Feb 14, 2026',
    totalSales: '-',
    segments: [seg('Cart recovery'), seg('Win-back')],
    emailsOpened: 3,
    openRate: 12,
    emailsClicked: 0,
    clickRate: 0,
    signUpSource: 'Added manually',
    signUpDate: 'Dec 20, 2025',
    dataFields: {},
  },
  {
    email: 'virginia.woolf@modernist.com',
    name: 'Virginia Woolf',
    status: 'Unsubscribed',
    lastActivity: 'Jan 30, 2026',
    totalSales: '-',
    segments: [seg('Holiday sale'), seg('Win-back')],
    emailsOpened: 8,
    openRate: 25,
    emailsClicked: 2,
    clickRate: 6,
    signUpSource: 'Integration',
    signUpDate: 'Sep 15, 2025',
    dataFields: { phoneNumber: '555-0199' },
  },
  {
    email: 'hemingway@writerhub.com',
    name: 'Ernest Hemingway',
    status: 'Active',
    lastActivity: 'Mar 17, 2026',
    totalSales: '$340.00',
    segments: [
      seg('Product launch'),
      seg('Weekly digest'),
      seg('Early access'),
      seg('Beta testers'),
      seg('VIP customers'),
    ],
    emailsOpened: 60,
    openRate: 88,
    emailsClicked: 35,
    clickRate: 51,
    signUpSource: 'Form opt-in',
    signUpDate: 'Jun 01, 2024',
    dataFields: { website: 'hemingway.io', company: 'Hemingway Press', birthday: 'Jul 21' },
  },
  {
    email: 'leo.tolstoy@literature.org',
    name: 'Leo Tolstoy',
    status: 'Archived',
    lastActivity: 'Jul 30, 2025',
    totalSales: '$63.40',
    segments: [seg('Spring sale')],
    emailsOpened: 15,
    openRate: 40,
    emailsClicked: 5,
    clickRate: 13,
    signUpSource: 'CSV import',
    signUpDate: 'Apr 10, 2025',
    dataFields: {},
  },
  {
    email: 'f.scott.fitzgerald@classicwriter.com',
    name: 'F. Scott Fitzgerald',
    status: 'Active',
    lastActivity: 'Mar 10, 2026',
    totalSales: '$120.00',
    segments: [
      seg('Profile social form'),
      seg('Refer a friend'),
      seg('Instagram followers'),
      seg('Black Friday'),
    ],
    emailsOpened: 22,
    openRate: 60,
    emailsClicked: 9,
    clickRate: 24,
    signUpSource: 'Checkout',
    signUpDate: 'Aug 18, 2025',
    dataFields: { company: 'Gatsby Inc' },
  },
  {
    email: 'charles.dickens@example.com',
    name: 'Charles Dickens',
    status: 'Active',
    lastActivity: 'Mar 16, 2026',
    totalSales: '$54.20',
    segments: [seg('Welcome series'), seg('Weekly digest')],
    emailsOpened: 38,
    openRate: 67,
    emailsClicked: 14,
    clickRate: 25,
    signUpSource: 'Form opt-in',
    signUpDate: 'Oct 02, 2024',
    dataFields: { birthday: 'Feb 07' },
  },
  {
    email: 'emily.bronte@moorland.com',
    name: 'Emily Bronte',
    status: 'Active',
    lastActivity: 'Mar 19, 2026',
    totalSales: '$210.80',
    segments: [
      seg('VIP customers'),
      seg('May promotion'),
      seg('Loyalty rewards'),
      seg('Refer a friend'),
      seg('Birthday club'),
    ],
    emailsOpened: 55,
    openRate: 90,
    emailsClicked: 30,
    clickRate: 49,
    signUpSource: 'Form opt-in',
    signUpDate: 'Mar 15, 2024',
    dataFields: {
      website: 'emilybronte.co.uk',
      company: 'Wuthering Heights LLC',
      phoneNumber: '555-0301',
    },
  },
  {
    email: 'oscar.wilde@aesthete.io',
    name: 'Oscar Wilde',
    status: 'Unconfirmed',
    lastActivity: 'Resend opt-in',
    totalSales: '-',
    segments: [seg('Early access'), seg('Instagram followers')],
    emailsOpened: 0,
    openRate: 0,
    emailsClicked: 0,
    clickRate: 0,
    signUpSource: 'Added manually',
    signUpDate: 'Mar 10, 2026',
    dataFields: { company: 'Dorian Gray Studios' },
  },
  {
    email: 'maya.angelou@poetry.net',
    name: 'Maya Angelou',
    status: 'Active',
    lastActivity: 'Mar 14, 2026',
    totalSales: '$450.00',
    segments: [
      seg('VIP customers'),
      seg('Profile social form'),
      seg('Webinar attendees'),
      seg('Product launch'),
      seg('Loyalty rewards'),
      seg('Holiday sale'),
    ],
    emailsOpened: 70,
    openRate: 95,
    emailsClicked: 42,
    clickRate: 57,
    signUpSource: 'Checkout',
    signUpDate: 'Jan 20, 2024',
    dataFields: { website: 'mayaangelou.org', birthday: 'Apr 04' },
  },
  {
    email: 'kafka@metamorphosis.dev',
    name: 'Franz Kafka',
    status: 'Bounced',
    lastActivity: 'Dec 05, 2025',
    totalSales: '$12.00',
    segments: [seg('Free trial'), seg('Cart recovery')],
    emailsOpened: 2,
    openRate: 8,
    emailsClicked: 0,
    clickRate: 0,
    signUpSource: 'Integration',
    signUpDate: 'Nov 30, 2025',
    dataFields: {},
  },
  {
    email: 'toni.morrison@beloved.com',
    name: 'Toni Morrison',
    status: 'Active',
    lastActivity: 'Mar 11, 2026',
    totalSales: '$88.50',
    segments: [seg('Summer campaign'), seg('Beta testers'), seg('Black Friday')],
    emailsOpened: 28,
    openRate: 58,
    emailsClicked: 11,
    clickRate: 23,
    signUpSource: 'Form opt-in',
    signUpDate: 'Jul 08, 2025',
    dataFields: { phoneNumber: '555-0455', company: 'Morrison Literary' },
  },
  {
    email: 'sylvia.plath@belljar.org',
    name: 'Sylvia Plath',
    status: 'Archived',
    lastActivity: 'Jun 20, 2025',
    totalSales: '$33.00',
    segments: [seg('Win-back'), seg('Spring sale')],
    emailsOpened: 10,
    openRate: 35,
    emailsClicked: 3,
    clickRate: 10,
    signUpSource: 'CSV import',
    signUpDate: 'May 01, 2025',
    dataFields: { birthday: 'Oct 27' },
  },
  {
    email: 'gabriel.marquez@macondo.co',
    name: 'Gabriel Garcia Marquez',
    status: 'Active',
    lastActivity: 'Mar 13, 2026',
    totalSales: '$275.60',
    segments: [
      seg('VIP customers'),
      seg('May promotion'),
      seg('Refer a friend'),
      seg('Product launch'),
      seg('Welcome series'),
    ],
    emailsOpened: 48,
    openRate: 78,
    emailsClicked: 20,
    clickRate: 33,
    signUpSource: 'Checkout',
    signUpDate: 'Feb 14, 2025',
    dataFields: { website: 'macondo.co', company: 'Cien Anos Corp' },
  },
  {
    email: 'haruki@windupbird.jp',
    name: 'Haruki Murakami',
    status: 'Unsubscribed',
    lastActivity: 'Oct 22, 2025',
    totalSales: '$42.10',
    segments: [seg('Instagram followers')],
    emailsOpened: 12,
    openRate: 30,
    emailsClicked: 4,
    clickRate: 10,
    signUpSource: 'Integration',
    signUpDate: 'Aug 05, 2025',
    dataFields: { website: 'murakami.jp' },
  },
  {
    email: 'chimamanda@americanah.ng',
    name: 'Chimamanda Adichie',
    status: 'Active',
    lastActivity: 'Mar 18, 2026',
    totalSales: '$165.00',
    segments: [
      seg('Profile social form'),
      seg('VIP customers'),
      seg('Weekly digest'),
      seg('Black Friday'),
    ],
    emailsOpened: 40,
    openRate: 75,
    emailsClicked: 22,
    clickRate: 41,
    signUpSource: 'Form opt-in',
    signUpDate: 'Apr 22, 2025',
    dataFields: { company: 'Purple Hibiscus Ltd', phoneNumber: '555-0678' },
  },
  {
    email: 'jorge.borges@labyrinths.ar',
    name: 'Jorge Luis Borges',
    status: 'Active',
    lastActivity: 'May 15, 2025',
    totalSales: '-',
    segments: [seg('Summer campaign'), seg('Free trial')],
    emailsOpened: 5,
    openRate: 15,
    emailsClicked: 1,
    clickRate: 3,
    signUpSource: 'Added manually',
    signUpDate: 'Mar 30, 2025',
    dataFields: {},
  },
];

// --- Sorting ---

type SortKey = 'email' | 'status' | 'lastActivity' | 'totalSales';
type SortDir = 'asc' | 'desc';

function parseSales(val: string): number {
  if (val === '-') return -Infinity;
  return parseFloat(val.replace('$', ''));
}

function parseActivityDate(val: string): number {
  if (val === 'Resend opt-in') return -Infinity;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? -Infinity : d.getTime();
}

const CURRENT_YEAR = new Date().getFullYear();

function formatLastActivity(val: string): string {
  if (val === 'Resend opt-in') return val;
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  const month = d.toLocaleString('en-US', { month: 'short' });
  const day = d.getDate();
  if (d.getFullYear() === CURRENT_YEAR) return `${month} ${day}`;
  return `${month} ${day}, ${d.getFullYear()}`;
}

function sortSubscribers(list: Subscriber[], key: SortKey, dir: SortDir): Subscriber[] {
  const sorted = [...list].sort((a, b) => {
    let cmp = 0;
    if (key === 'totalSales') {
      cmp = parseSales(a.totalSales) - parseSales(b.totalSales);
    } else if (key === 'lastActivity') {
      cmp = parseActivityDate(a.lastActivity) - parseActivityDate(b.lastActivity);
    } else {
      cmp = a[key].localeCompare(b[key]);
    }
    return dir === 'asc' ? cmp : -cmp;
  });
  return sorted;
}

// --- Styles ---

const pageStyles = css`
  min-height: 100vh;
  background: var(--grn-color-background2);
  overflow-y: scroll;
`;

const contentStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--grn-space-m);
  padding: 72px;
`;

const headerRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1140px;
`;

const tabsStyles = css`
  display: flex;
  align-items: center;
  gap: var(--grn-space-l);
`;

const tabStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28.63px;
  color: var(--grn-color-content2);
  cursor: pointer;
`;

const tabActiveStyles = css`
  color: var(--grn-color-content);
`;

const filtersStyles = css`
  display: flex;
  align-items: center;
  gap: var(--grn-space-s);
`;

const textButtonStyles = css`
  display: flex;
  align-items: center;
  gap: var(--grn-space-s);
  height: 36px;
  padding: 0 var(--grn-space-s);
  border-radius: var(--grn-radius-s);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  cursor: pointer;
  border: none;
  background: none;

  svg {
    color: var(--grn-color-icon);
  }

  &:hover {
    background: var(--grn-color-shade3);
  }
`;

const iconButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 8px;
  border-radius: var(--grn-radius-s);
  cursor: pointer;
  border: none;
  background: none;
  color: var(--grn-color-icon);

  &:hover {
    background: var(--grn-color-shade3);
  }
`;

const addButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 40px;
  padding: 0 17px;
  border-radius: var(--grn-radius-s);
  background: var(--grn-color-shade12);
  color: var(--grn-color-shade2);
  border: 1px solid transparent;
  cursor: pointer;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: var(--grn-text-m);
  line-height: 20px;
  margin-left: var(--grn-space-s);

  &:hover {
    opacity: 0.9;
  }
`;

const tableStyles = css`
  width: 100%;
  max-width: 1156px;
  border-collapse: collapse;
  table-layout: fixed;
`;

const thStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  text-align: left;
  padding: 8px;
  padding-bottom: 16px;
  height: 36px;
  white-space: nowrap;
  vertical-align: middle;
`;

const thSortableStyles = css`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: var(--grn-color-icon);
  }
`;

const thSubscriberStyles = css`
  width: auto;
`;

const thStatusStyles = css`
  width: 120px;
`;

const thActivityStyles = css`
  width: 136px;
`;

const thSalesStyles = css`
  width: 104px;
`;

const thSegmentsStyles = css`
  width: 280px;
  text-align: right;
  padding-right: 8px;
`;

const rowStyles = css`
  border-bottom: 1px solid var(--grn-color-shade3);
  height: 56px;
`;

const cellStyles = css`
  padding: 0 8px;
  vertical-align: middle;
`;

const emailStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const nameStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content2);
  white-space: nowrap;
  margin-left: 16px;
`;

const statusBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 var(--grn-space-s2);
  border-radius: 112px;
  background: var(--grn-color-shade3);
  margin-left: -12px;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  color: var(--grn-color-content);
`;

const statusBadgeActiveStyles = css`
  background: var(--grn-color-backgroundSuccessSubtle);
  color: var(--grn-color-onBackgroundSuccessSubtle);
`;

const activityStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const resendLinkStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content2);
  text-decoration: underline;
  cursor: pointer;
`;

const salesStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const segmentsCellStyles = css`
  text-align: right;
  padding-right: 8px;
  vertical-align: middle;
`;

const segmentsWrapStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--grn-space-s);
  flex-wrap: nowrap;
`;

const segmentBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 var(--grn-space-s);
  border-radius: var(--grn-radius-xs);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  color: var(--grn-color-content);
  white-space: nowrap;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const extraBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 var(--grn-space-s);
  border-radius: var(--grn-radius-xs);
  background: var(--grn-color-shade4);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  color: var(--grn-color-content);
  white-space: nowrap;
  letter-spacing: -0.12px;
`;

const filterDropdownStyles = css`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  width: 216px;
  padding: 8px 0;
  background: var(--grn-color-background);
  border-radius: var(--grn-radius-m);
  box-shadow: var(--grn-shadow-m);
  z-index: 20;
  transform-origin: top right;
  transition:
    opacity var(--grn-transition-fast),
    transform var(--grn-transition-fast);
`;

const filterDropdownHiddenStyles = css`
  opacity: 0;
  transform: scale(0.95);
  pointer-events: none;
`;

const filterDropdownVisibleStyles = css`
  opacity: 1;
  transform: scale(1);
`;

const filterGroupStyles = css`
  padding: 0 var(--grn-space-s);
`;

const filterDividerStyles = css`
  padding: 8px 0;

  &::after {
    content: '';
    display: block;
    height: 1px;
    background: var(--grn-color-border);
  }
`;

const filterDividerFullWidthStyles = css`
  padding: 8px 0;
  margin: 0 calc(-1 * var(--grn-space-s));

  &::after {
    content: '';
    display: block;
    height: 1px;
    background: var(--grn-color-border);
  }
`;

const filterItemStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: var(--grn-radius-s);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  cursor: pointer;
  width: 100%;
  border: none;
  background: none;
  text-align: left;

  &:hover {
    background: var(--grn-color-shade3);
  }
`;

const filterSegmentBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 var(--grn-space-s);
  border-radius: var(--grn-radius-xs);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  color: var(--grn-color-content);
  white-space: nowrap;
`;

const filterItemActiveStyles = css`
  background: var(--grn-color-shade3);
`;

const filterItemLabelStyles = css`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const filterItemChevronStyles = css`
  color: var(--grn-color-icon);
  flex-shrink: 0;
`;

// --- Filter sub-panel styles ---

const subPanelBaseStyles = css`
  width: 216px;
  padding: 8px 0;
  background: var(--grn-color-background);
  border-radius: var(--grn-radius-m);
  box-shadow: var(--grn-shadow-m);
  transition:
    opacity var(--grn-transition-fast),
    transform var(--grn-transition-fast);
`;

const subPanelStyles = css`
  ${subPanelBaseStyles}
  position: absolute;
  left: calc(100% + 4px);
  top: 0;
  z-index: 21;
  transform-origin: top left;
`;

const subPanelHiddenStyles = css`
  opacity: 0;
  transform: scale(0.95);
  pointer-events: none;
`;

const subPanelVisibleStyles = css`
  opacity: 1;
  transform: scale(1);
`;

const conditionButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 var(--grn-space-s);
  border-radius: var(--grn-radius-s);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  cursor: pointer;
  border: none;
  background: none;
  margin: 0 8px;

  svg {
    color: var(--grn-color-icon);
  }

  &:hover {
    background: var(--grn-color-shade3);
  }
`;

const conditionItemStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: var(--grn-radius-s);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  cursor: pointer;
  width: 100%;
  border: none;
  background: none;
  text-align: left;

  &:hover {
    background: var(--grn-color-shade3);
  }
`;

const conditionCheckStyles = css`
  margin-left: auto;
  color: var(--grn-color-content);
  flex-shrink: 0;
`;

const searchInputStyles = css`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  width: calc(100% - 16px);
  margin: 0 8px;
  border-radius: var(--grn-radius-s);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  border: none;
  background: none;
  outline: none;

  &::placeholder {
    color: var(--grn-color-content2);
  }
`;

const multiSelectInputWrapStyles = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 40px;
  padding: 6px 12px;
  width: calc(100% - 16px);
  margin: 0 8px;
`;

const multiSelectInputStyles = css`
  border: none;
  background: none;
  outline: none;
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: var(--grn-color-content);
  flex: 1;
  min-width: 60px;
  padding: 0;

  &::placeholder {
    color: var(--grn-color-content2);
  }
`;

const selectedBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  border-radius: var(--grn-radius-xs);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.8px;
  color: var(--grn-color-content);
  white-space: nowrap;
  flex-shrink: 0;
`;

const selectedBadgeCloseStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.6;
  border: none;
  background: none;
  padding: 0;
  color: inherit;

  &:hover {
    opacity: 1;
  }
`;

// --- Pagination styles ---

const ITEMS_PER_PAGE = 10;

const loadingContainerStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1156px;
  min-height: 400px;
`;

const spinnerStyles = css`
  width: 24px;
  height: 24px;
  border: 2px solid var(--grn-color-shade4);
  border-top-color: var(--grn-color-content);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const paginationStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--grn-space-xs);
  width: 100%;
  max-width: 1156px;
  padding: var(--grn-space-m) 0;
`;

const paginationButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: var(--grn-radius-s);
  background: none;
  color: var(--grn-color-content);
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: var(--grn-text-m);
  cursor: pointer;
  transition: background var(--grn-transition-fast);

  &:hover:not(:disabled) {
    background: var(--grn-color-shade3);
  }

  &:disabled {
    color: var(--grn-color-content2);
    cursor: default;
    opacity: 0.4;
  }
`;

const paginationActiveStyles = css`
  background: var(--grn-color-shade12);
  color: var(--grn-color-shade2);

  &:hover:not(:disabled) {
    background: var(--grn-color-shade12);
  }
`;

// --- Filter menu data ---

type SegmentItem = { label: string; color: string };

type SubPanelConfig = {
  conditions?: { label: string; active?: boolean }[];
  searchPlaceholder?: string;
  items?: string[];
  checkable?: boolean;
  dividerAfter?: number;
  singleValue?: boolean;
  suffix?: string;
  segmentItems?: SegmentItem[];
  wide?: boolean;
  multiSelect?: boolean;
};

// Extract unique segments from subscriber data
const allSegments: SegmentItem[] = [];
const seenSegments = new Set<string>();
subscribers.forEach(sub => {
  sub.segments.forEach(s => {
    const clean = s.label.replace(/\.{3}$/, '');
    if (!seenSegments.has(clean)) {
      seenSegments.add(clean);
      allSegments.push({ label: clean, color: s.color });
    }
  });
});

const filterSubPanels: Record<string, SubPanelConfig> = {
  Status: {
    items: [
      'Active',
      'Unconfirmed',
      'Bounced',
      'Unsubscribed',
      'Marked spam',
      'Cleaned',
      'Archived',
    ],
    checkable: true,
  },
  Segments: {
    conditions: [{ label: 'Is', active: true }, { label: 'Is not' }],
    searchPlaceholder: 'Search segments',
    segmentItems: allSegments,
    wide: true,
    multiSelect: true,
  },
  'Last activity': {
    dividerAfter: 6,
    items: [
      'In the last 24 hours',
      'In the last 7 days',
      'In the last 30 days',
      'In the last 60 days',
      'In the last 90 days',
      'In the last 6 months',
      'In the last year',
      '1 day or more ago',
      '7 or more days ago',
      '30 or more days ago',
      '60 or more days ago',
      '90 or more days ago',
      '6 or more months ago',
      'A year or more ago',
    ],
  },
  'Emails open': {
    conditions: [
      { label: 'More than', active: true },
      { label: 'Equal to' },
      { label: 'Less than' },
    ],
    searchPlaceholder: 'Type number of emails',
    items: [
      'All time',
      'In the last 12 hours',
      'In the last 24 hours',
      'In the last 7 days',
      'In the last 30 days',
      'In the last 60 days',
      'In the last 90 days',
    ],
  },
  'Open rate': {
    conditions: [
      { label: 'More than', active: true },
      { label: 'Equal to' },
      { label: 'Less than' },
    ],
    searchPlaceholder: 'Type open rate',
    singleValue: true,
    suffix: '%',
  },
  'Emails clicked': {
    conditions: [
      { label: 'More than', active: true },
      { label: 'Equal to' },
      { label: 'Less than' },
    ],
    searchPlaceholder: 'Type number of emails',
    items: [
      'All time',
      'In the last 12 hours',
      'In the last 24 hours',
      'In the last 7 days',
      'In the last 30 days',
      'In the last 60 days',
      'In the last 90 days',
    ],
  },
  'Click rate': {
    conditions: [
      { label: 'More than', active: true },
      { label: 'Equal to' },
      { label: 'Less than' },
    ],
    searchPlaceholder: 'Type click rate',
    singleValue: true,
    suffix: '%',
  },
  'Email address': {
    conditions: [{ label: 'Contains', active: true }, { label: 'Does not contain' }],
    searchPlaceholder: 'Search emails',
    items: subscribers.map(s => s.email),
    wide: true,
    multiSelect: true,
  },
  'Data field': {
    items: ['Website', 'Phone number', 'Company', 'Birthday'],
  },
  'Sign up source': {
    conditions: [{ label: 'Is', active: true }, { label: 'Is not' }],
    items: ['Added manually', 'CSV import', 'Form opt-in', 'Integration', 'Checkout'],
  },
  'Sign up date': {
    conditions: [{ label: 'In the last', active: true }, { label: 'Not in the last' }],
    searchPlaceholder: 'Type number of days',
  },
  'Filter groups': {
    items: [],
  },
};

const filterGroups = [
  ['Status', 'Segments', 'Last activity'],
  ['Emails open', 'Open rate', 'Emails clicked', 'Click rate'],
  ['Email address', 'Data field', 'Sign up source', 'Sign up date'],
  ['Filter groups'],
];

const checkboxStyles = css`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1.5px solid var(--grn-color-shade6);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background var(--grn-transition-fast),
    border-color var(--grn-transition-fast);
`;

const checkboxCheckedStyles = css`
  background: var(--grn-color-shade12);
  border-color: var(--grn-color-shade12);
  color: var(--grn-color-shade2);
`;

// --- Applied filters styles ---

const appliedFiltersRowStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 1140px;
  flex-wrap: wrap;
`;

const filterChipStyles = css`
  display: flex;
  align-items: center;
  gap: var(--grn-space-s2);
  height: 36px;
  padding: 0 var(--grn-space-s2);
  background: #ebf3ff;
  border-radius: var(--grn-radius-s);
`;

const filterChipLabelStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 500;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: #094cc8;
  white-space: nowrap;
  flex-shrink: 0;
`;

const filterChipDividerStyles = css`
  width: 1px;
  height: 100%;
  background: #cce1ff;
  flex-shrink: 0;
`;

const filterChipConditionStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: #094cc8;
  white-space: nowrap;
  flex-shrink: 0;
`;

const filterChipValueStyles = css`
  font-family: 'Flodesk Sans', sans-serif;
  font-weight: 400;
  font-size: var(--grn-text-m);
  line-height: 20px;
  color: #094cc8;
  white-space: nowrap;
  min-width: 0;
`;

const filterChipCloseStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  border: none;
  background: none;
  cursor: pointer;
  color: #094cc8;
  flex-shrink: 0;
  width: 33px;
  padding: 0 12px;
  margin: 0 calc(-1 * var(--grn-space-s2));
  border-radius: 0 var(--grn-radius-s) var(--grn-radius-s) 0;
  transition: background var(--grn-transition-fast);

  &:hover {
    background: rgba(9, 76, 200, 0.1);
  }
`;

const filterChipClickableStyles = css`
  cursor: pointer;
  padding: 0 var(--grn-space-s2);
  margin: 0 calc(-1 * var(--grn-space-s2));
  transition: background var(--grn-transition-fast);
  align-self: stretch;
  display: flex;
  align-items: center;

  &:hover {
    background: rgba(9, 76, 200, 0.1);
  }
`;

const chipDropdownStyles = css`
  position: absolute;
  top: 36px;
  left: 0;
  width: 216px;
  padding: 8px 0;
  background: var(--grn-color-background);
  border-radius: var(--grn-radius-m);
  box-shadow: var(--grn-shadow-m);
  z-index: 30;
  transform-origin: top left;
  transition:
    opacity var(--grn-transition-fast),
    transform var(--grn-transition-fast);
`;

const addFilterIconButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: var(--grn-radius-s);
  color: var(--grn-color-content);
  background: var(--grn-color-shade3);
  cursor: pointer;
  padding: 0;

  &:hover {
    background: var(--grn-color-shade4);
  }

  &:hover {
    background: var(--grn-color-shade3);
  }
`;

// --- Check icon ---

function CheckIcon() {
  return (
    <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
      <path
        d="M1 5L5 9L13 1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// --- Condition dropdown component ---

function ConditionDropdown({
  conditions,
  activeCondition,
  onSelect,
  visible,
}: {
  conditions: { label: string; active?: boolean }[];
  activeCondition: string;
  onSelect: (label: string) => void;
  visible: boolean;
}) {
  return (
    <div
      css={[
        subPanelStyles,
        css`
          right: auto;
          left: 50%;
          top: 0;
          width: 200px;
          z-index: 22;
          transform-origin: top center;
          transform: translateX(-50%) ${visible ? 'scale(1)' : 'scale(0.95)'};
          opacity: ${visible ? 1 : 0};
          pointer-events: ${visible ? 'auto' : 'none'};
        `,
      ]}
    >
      <div css={filterGroupStyles}>
        {conditions.map(c => (
          <button
            type="button"
            key={c.label}
            css={conditionItemStyles}
            onClick={() => onSelect(c.label)}
          >
            <span css={filterItemLabelStyles}>{c.label}</span>
            {c.label === activeCondition && (
              <span css={conditionCheckStyles}>
                <CheckIcon />
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Sub-panel component ---

function FilterSubPanel({
  config,
  visible,
  category,
  onApplyFilter,
  appliedFilters,
}: {
  config: SubPanelConfig;
  visible: boolean;
  category: string;
  onApplyFilter: (category: string, condition: string | undefined, values: string[]) => void;
  appliedFilters: AppliedFilter[];
}) {
  const [activeCondition, setActiveCondition] = useState(
    () => config.conditions?.find(c => c.active)?.label || config.conditions?.[0]?.label,
  );
  const [conditionOpen, setConditionOpen] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(() => {
    if (!config.checkable) return new Set();
    const existing = appliedFilters.find(f => f.category === category);
    return new Set(existing?.values || []);
  });
  const [searchValue, setSearchValue] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>(() => {
    if (!config.multiSelect) return [];
    const existing = appliedFilters.find(f => f.category === category);
    return existing?.values || [];
  });

  const addSelectedItem = (item: string) => {
    if (selectedItems.includes(item)) return;
    const next = [...selectedItems, item];
    setSelectedItems(next);
    onApplyFilter(category, activeCondition, next);
  };

  const removeSelectedItem = (item: string) => {
    const next = selectedItems.filter(i => i !== item);
    setSelectedItems(next);
    if (next.length > 0) {
      onApplyFilter(category, activeCondition, next);
    } else {
      onApplyFilter(category, undefined, []);
    }
  };

  // Sync checkedItems when applied filters change externally (e.g. chip removed)
  useEffect(() => {
    if (!config.checkable) return;
    const filterForCategory = appliedFilters.find(f => f.category === category);
    const externalValues = new Set(filterForCategory?.values || []);
    setCheckedItems(prev => {
      if (prev.size === externalValues.size && [...prev].every(v => externalValues.has(v))) {
        return prev;
      }
      return externalValues;
    });
  }, [appliedFilters, category, config.checkable]);

  // Sync selectedItems when applied filters change externally
  useEffect(() => {
    if (!config.multiSelect) return;
    const existing = appliedFilters.find(f => f.category === category);
    setSelectedItems(existing?.values || []);
  }, [appliedFilters, category, config.multiSelect]);

  const toggleChecked = (item: string) => {
    const next = new Set(checkedItems);
    if (next.has(item)) next.delete(item);
    else next.add(item);
    setCheckedItems(next);
    onApplyFilter(category, undefined, Array.from(next));
  };

  const hasConditions = config.conditions && config.conditions.length > 0;
  const hasSearch = !!config.searchPlaceholder;
  const hasItems = config.items && config.items.length > 0;
  const isDataField = category === 'Data field';

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchValue.trim()) {
      let val = searchValue.trim().replace(/%$/, '');
      if (config.suffix) val = `${val}${config.suffix}`;
      onApplyFilter(category, activeCondition, [val]);
      setSearchValue('');
    }
  };

  const handleItemClick = (item: string) => {
    if (config.checkable) return; // handled by toggleChecked
    if (isDataField) return; // Data field top-level items just navigate deeper
    if (config.multiSelect) {
      addSelectedItem(item);
      setSearchValue('');
      return;
    }
    // For Last activity, Sign up source, etc. — apply immediately
    if (hasConditions) {
      onApplyFilter(category, activeCondition, [item]);
    } else {
      onApplyFilter(category, undefined, [item]);
    }
  };

  return (
    <div
      css={[
        subPanelBaseStyles,
        css`
          position: relative;
        `,
        config.wide &&
          css`
            width: 320px;
          `,
        visible ? subPanelVisibleStyles : subPanelHiddenStyles,
      ]}
    >
      {hasConditions && (
        <>
          <div
            css={css`
              position: relative;
              padding: 0 var(--grn-space-s);
            `}
          >
            <button
              type="button"
              css={conditionButtonStyles}
              onClick={e => {
                e.stopPropagation();
                setConditionOpen(!conditionOpen);
              }}
            >
              {activeCondition}
              <ChevronDownIcon />
            </button>
            {config.conditions && (
              <ConditionDropdown
                conditions={config.conditions}
                activeCondition={activeCondition || ''}
                onSelect={label => {
                  setActiveCondition(label);
                  setConditionOpen(false);
                }}
                visible={conditionOpen}
              />
            )}
          </div>
          <div css={filterDividerStyles} />
        </>
      )}
      {hasSearch && (
        <>
          {config.multiSelect ? (
            <div
              role="presentation"
              css={multiSelectInputWrapStyles}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
            >
              {selectedItems.map(item => {
                const segColor = config.segmentItems?.find(si => si.label === item)?.color;
                return (
                  <span
                    key={item}
                    css={selectedBadgeStyles}
                    style={{ background: segColor || 'var(--grn-color-shade3)' }}
                  >
                    {item}
                    <button
                      type="button"
                      aria-label={`Remove ${item}`}
                      css={selectedBadgeCloseStyles}
                      onClick={() => removeSelectedItem(item)}
                    >
                      <CrossIcon />
                    </button>
                  </span>
                );
              })}
              <input
                type="text"
                css={multiSelectInputStyles}
                placeholder={selectedItems.length === 0 ? config.searchPlaceholder : ''}
                aria-label={config.searchPlaceholder}
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && searchValue.trim()) {
                    addSelectedItem(searchValue.trim());
                    setSearchValue('');
                  } else if (e.key === 'Backspace' && !searchValue && selectedItems.length > 0) {
                    removeSelectedItem(selectedItems[selectedItems.length - 1]);
                  }
                }}
              />
            </div>
          ) : (
            <input
              type="text"
              css={searchInputStyles}
              placeholder={config.searchPlaceholder}
              aria-label={config.searchPlaceholder}
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              onClick={e => e.stopPropagation()}
            />
          )}
          {(hasItems || config.segmentItems) && <div css={filterDividerStyles} />}
        </>
      )}
      {config.segmentItems && (
        <div
          css={[
            filterGroupStyles,
            css`
              max-height: 400px;
              overflow-y: auto;
            `,
          ]}
        >
          {config.segmentItems
            .filter(s => !selectedItems.includes(s.label))
            .filter(s => !searchValue || s.label.toLowerCase().includes(searchValue.toLowerCase()))
            .map(s => (
              <button
                type="button"
                key={s.label}
                css={filterItemStyles}
                onClick={() => {
                  if (config.multiSelect) {
                    addSelectedItem(s.label);
                    setSearchValue('');
                  } else if (hasConditions) {
                    onApplyFilter(category, activeCondition, [s.label]);
                  } else {
                    onApplyFilter(category, undefined, [s.label]);
                  }
                }}
              >
                <span css={filterSegmentBadgeStyles} style={{ background: s.color }}>
                  {s.label}
                </span>
              </button>
            ))}
        </div>
      )}
      {hasItems && (
        <div
          css={[
            filterGroupStyles,
            hasSearch &&
              css`
                max-height: 400px;
                overflow-y: auto;
              `,
          ]}
        >
          {(config.multiSelect
            ? config.items!.filter(i => !selectedItems.includes(i))
            : config.items!
          )
            .filter(
              i =>
                !hasSearch || !searchValue || i.toLowerCase().includes(searchValue.toLowerCase()),
            )
            .map((item, idx) => (
              <React.Fragment key={item}>
                {!searchValue &&
                  config.dividerAfter !== undefined &&
                  idx === config.dividerAfter + 1 && <div css={filterDividerFullWidthStyles} />}
                <button
                  type="button"
                  css={filterItemStyles}
                  onClick={e => {
                    if (config.checkable) {
                      e.stopPropagation();
                      toggleChecked(item);
                    } else {
                      handleItemClick(item);
                    }
                  }}
                >
                  {config.checkable && (
                    <span css={[checkboxStyles, checkedItems.has(item) && checkboxCheckedStyles]}>
                      {checkedItems.has(item) && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path
                            d="M1 4L3.5 6.5L9 1"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                  )}
                  <span css={filterItemLabelStyles}>{item}</span>
                </button>
              </React.Fragment>
            ))}
        </div>
      )}
    </div>
  );
}

// --- Chip values dropdown ---

function ChipValuesDropdown({
  filter,
  config,
  onUpdate,
  onClose,
}: {
  filter: AppliedFilter;
  config: SubPanelConfig;
  onUpdate: (values: string[]) => void;
  onClose: () => void;
}) {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(() => new Set(filter.values));
  const [searchValue, setSearchValue] = useState('');

  const toggleChecked = (item: string) => {
    const next = new Set(checkedItems);
    if (next.has(item)) next.delete(item);
    else next.add(item);
    setCheckedItems(next);
    onUpdate(Array.from(next));
  };

  const hasItems = config.items && config.items.length > 0;

  return (
    <div
      css={[
        chipDropdownStyles,
        css`
          left: calc(-1 * var(--grn-space-s2) + 10px);
        `,
      ]}
    >
      {config.searchPlaceholder && (
        <>
          <input
            type="text"
            css={searchInputStyles}
            placeholder={config.searchPlaceholder}
            aria-label="Filter value"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && searchValue.trim()) {
                let val = searchValue.trim().replace(/%$/, '');
                if (config.suffix) val = `${val}${config.suffix}`;
                if (config.singleValue) {
                  onUpdate([val]);
                } else {
                  onUpdate([...filter.values, val]);
                }
                setSearchValue('');
                onClose();
              }
            }}
            onClick={e => e.stopPropagation()}
          />
          {hasItems && <div css={filterDividerStyles} />}
        </>
      )}
      {hasItems && (
        <div css={filterGroupStyles}>
          {config.items!.map((item, idx) => (
            <React.Fragment key={item}>
              {config.dividerAfter !== undefined && idx === config.dividerAfter + 1 && (
                <div css={filterDividerFullWidthStyles} />
              )}
              <button
                type="button"
                css={filterItemStyles}
                onClick={e => {
                  e.stopPropagation();
                  if (config.checkable) {
                    toggleChecked(item);
                  } else {
                    onUpdate([item]);
                    onClose();
                  }
                }}
              >
                {config.checkable && (
                  <span css={[checkboxStyles, checkedItems.has(item) && checkboxCheckedStyles]}>
                    {checkedItems.has(item) && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 4L3.5 6.5L9 1"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                )}
                <span css={filterItemLabelStyles}>{item}</span>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Segments cell with overflow ---

const measureContainerStyles = css`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  display: flex;
  gap: var(--grn-space-s);
  flex-wrap: nowrap;
  white-space: nowrap;
`;

function SegmentsCell({ segments }: { segments: { label: string; color: string }[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(segments.length);

  useEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      const measureEl = measureRef.current;
      if (!container || !measureEl) return;

      const containerWidth = container.offsetWidth;
      const badges = Array.from(measureEl.children) as HTMLElement[];
      if (badges.length === 0) return;

      const gap = 8;
      const counterWidth = 36;
      let usedWidth = 0;
      let fits = 0;

      for (let i = 0; i < badges.length; i += 1) {
        const badgeWidth = badges[i].offsetWidth;
        const widthWithThis = usedWidth + badgeWidth + (fits > 0 ? gap : 0);
        const remaining = badges.length - (i + 1);

        if (remaining > 0) {
          if (widthWithThis + gap + counterWidth > containerWidth && fits > 0) break;
        } else if (widthWithThis > containerWidth && fits > 0) {
          break;
        }

        usedWidth = widthWithThis;
        fits += 1;
      }

      setVisibleCount(Math.max(1, fits));
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [segments]);

  const hidden = segments.length - visibleCount;

  return (
    <div ref={containerRef} css={segmentsWrapStyles}>
      {/* Hidden measuring row with all badges */}
      <div ref={measureRef} css={measureContainerStyles}>
        {segments.map(s => (
          <span key={s.label} css={segmentBadgeStyles} style={{ background: s.color }}>
            {s.label}
          </span>
        ))}
      </div>
      {/* Visible badges */}
      {segments.slice(0, visibleCount).map(s => (
        <span key={s.label} css={segmentBadgeStyles} style={{ background: s.color }}>
          {s.label}
        </span>
      ))}
      {hidden > 0 && <span css={extraBadgeStyles}>+ {hidden}</span>}
    </div>
  );
}

// --- Component ---

function SortArrow({
  column,
  sortKey,
  sortDir,
}: {
  column: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
}) {
  if (column !== sortKey) return null;
  return sortDir === 'asc' ? <ArrowUpIcon /> : <ArrowDownIcon />;
}

export default function Home() {
  const [slowMode, setSlowMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const loadingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersEverApplied = useRef(false);
  const [sortKey, setSortKey] = useState<SortKey>('email');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [menuSource, setMenuSource] = useState<'filter' | 'plus' | null>(null);
  const [menuFixedPos, setMenuFixedPos] = useState<{ top: number; left: number } | null>(null);
  const [hoveredFilter, setHoveredFilter] = useState<string | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const plusBtnRef = useRef<HTMLButtonElement>(null);
  const safeTriangle = useSubmenuSafeTriangle();

  // Applied filters state
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
  const [editingChip, setEditingChip] = useState<{
    id: string;
    section: 'condition' | 'values';
  } | null>(null);
  const editingChipRef = useRef<HTMLDivElement>(null);

  const removeFilter = useCallback((id: string) => {
    filtersEverApplied.current = true;
    setAppliedFilters(prev => prev.filter(f => f.id !== id));
  }, []);

  const updateFilter = useCallback(
    (id: string, updates: Partial<Pick<AppliedFilter, 'condition' | 'values'>>) => {
      setAppliedFilters(prev =>
        prev.map(f => {
          if (f.id !== id) return f;
          const updated = { ...f, ...updates };
          return updated.values.length === 0 ? f : updated;
        }),
      );
    },
    [],
  );

  const hasFilters = appliedFilters.length > 0;

  const handleApplyFilter = useCallback(
    (category: string, condition: string | undefined, values: string[]) => {
      filtersEverApplied.current = true;
      setAppliedFilters(prev => {
        const panelConfig = filterSubPanels[category];
        const existing = prev.find(f => f.category === category);
        if (values.length === 0) {
          return prev.filter(f => f.category !== category);
        }
        // Single-value or checkable: replace existing filter for this category
        if (
          existing &&
          (category === 'Status' || panelConfig?.singleValue || panelConfig?.multiSelect)
        ) {
          return prev.map(f => (f.category === category ? { ...f, condition, values } : f));
        }
        // Add new filter
        return [...prev, { id: Date.now().toString(), category, condition, values }];
      });
      // Reset hovered filter for non-checkable, non-multiSelect so submenu closes
      const panelCfg = filterSubPanels[category];
      if (category !== 'Status' && !panelCfg?.multiSelect) {
        setHoveredFilter(null);
      }
    },
    [],
  );

  useEffect(() => {
    if (!filterMenuOpen) setMenuFixedPos(null);
  }, [filterMenuOpen]);

  useEffect(() => {
    if (!filterMenuOpen && !editingChip) return undefined;
    const handleClick = (e: MouseEvent) => {
      if (filterMenuOpen && filterRef.current && !filterRef.current.contains(e.target as Node)) {
        const clickedFilterBtn = filterBtnRef.current?.contains(e.target as Node);
        const clickedPlusBtn = plusBtnRef.current?.contains(e.target as Node);
        if (!clickedFilterBtn && !clickedPlusBtn) {
          setFilterMenuOpen(false);
          setMenuSource(null);
        }
      }
      if (
        editingChip &&
        editingChipRef.current &&
        !editingChipRef.current.contains(e.target as Node)
      ) {
        setEditingChip(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [filterMenuOpen, editingChip]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const filteredSubscribers = useMemo(() => {
    if (appliedFilters.length === 0) return subscribers;
    return subscribers.filter(sub =>
      appliedFilters.every(filter => {
        const val = filter.values.map(v => v.toLowerCase());
        const cond = filter.condition?.toLowerCase();

        switch (filter.category) {
          case 'Status':
            return val.includes(sub.status.toLowerCase());

          case 'Segments': {
            const subSegs = sub.segments.map(s => s.label.toLowerCase());
            if (cond === 'is') return val.some(v => subSegs.some(s => s.includes(v)));
            if (cond === 'is not') return !val.some(v => subSegs.some(s => s.includes(v)));
            return true;
          }

          case 'Last activity':
            return (
              val.includes(sub.lastActivity.toLowerCase()) ||
              val.some(v => {
                const subDate = parseActivityDate(sub.lastActivity);
                if (subDate === -Infinity) return false;
                const now = Date.now();
                const dayMs = 86400000;
                const thresholds: Record<string, number> = {
                  'in the last 24 hours': dayMs,
                  'in the last 7 days': 7 * dayMs,
                  'in the last 30 days': 30 * dayMs,
                  'in the last 60 days': 60 * dayMs,
                  'in the last 90 days': 90 * dayMs,
                  'in the last 6 months': 180 * dayMs,
                  'in the last year': 365 * dayMs,
                };
                const moreThresholds: Record<string, number> = {
                  '1 day or more ago': dayMs,
                  '7 or more days ago': 7 * dayMs,
                  '30 or more days ago': 30 * dayMs,
                  '60 or more days ago': 60 * dayMs,
                  '90 or more days ago': 90 * dayMs,
                  '6 or more months ago': 180 * dayMs,
                  'a year or more ago': 365 * dayMs,
                };
                if (thresholds[v]) return now - subDate <= thresholds[v];
                if (moreThresholds[v]) return now - subDate >= moreThresholds[v];
                return false;
              })
            );

          case 'Emails open': {
            const num = parseFloat(filter.values[0]);
            if (Number.isNaN(num)) return true;
            if (cond === 'more than') return sub.emailsOpened > num;
            if (cond === 'equal to') return sub.emailsOpened === num;
            if (cond === 'less than') return sub.emailsOpened < num;
            return true;
          }

          case 'Open rate': {
            const num = parseFloat(filter.values[0]);
            if (Number.isNaN(num)) return true;
            if (cond === 'more than') return sub.openRate > num;
            if (cond === 'equal to') return sub.openRate === num;
            if (cond === 'less than') return sub.openRate < num;
            return true;
          }

          case 'Emails clicked': {
            const num = parseFloat(filter.values[0]);
            if (Number.isNaN(num)) return true;
            if (cond === 'more than') return sub.emailsClicked > num;
            if (cond === 'equal to') return sub.emailsClicked === num;
            if (cond === 'less than') return sub.emailsClicked < num;
            return true;
          }

          case 'Click rate': {
            const num = parseFloat(filter.values[0]);
            if (Number.isNaN(num)) return true;
            if (cond === 'more than') return sub.clickRate > num;
            if (cond === 'equal to') return sub.clickRate === num;
            if (cond === 'less than') return sub.clickRate < num;
            return true;
          }

          case 'Email address': {
            const email = sub.email.toLowerCase();
            if (cond === 'contains') return val.some(v => email.includes(v));
            if (cond === 'does not contain') return !val.some(v => email.includes(v));
            return true;
          }

          case 'Sign up source': {
            const source = sub.signUpSource.toLowerCase();
            if (cond === 'is') return val.includes(source);
            if (cond === 'is not') return !val.includes(source);
            return true;
          }

          case 'Sign up date':
            // Simple text match for now
            return true;

          default:
            return true;
        }
      }),
    );
  }, [appliedFilters]);

  const sortedSubscribers = useMemo(
    () => sortSubscribers(filteredSubscribers, sortKey, sortDir),
    [filteredSubscribers, sortKey, sortDir],
  );

  // Reset page and trigger loading when filters change
  useEffect(() => {
    setCurrentPage(1);
    if (slowMode && filtersEverApplied.current) {
      setIsLoading(true);
      if (loadingTimeout.current) clearTimeout(loadingTimeout.current);
      loadingTimeout.current = setTimeout(() => {
        setIsLoading(false);
      }, 1500);
    }
  }, [appliedFilters, slowMode]);

  const totalPages = Math.ceil(sortedSubscribers.length / ITEMS_PER_PAGE);
  const paginatedSubscribers = useMemo(
    () => sortedSubscribers.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [sortedSubscribers, currentPage],
  );

  return (
    <div css={pageStyles}>
      <Header slowMode={slowMode} onToggleSlowMode={() => setSlowMode(!slowMode)} />
      <div css={contentStyles}>
        <div css={headerRowStyles}>
          <div css={tabsStyles}>
            <span css={[tabStyles, tabActiveStyles]}>Subscribers</span>
            <span css={tabStyles}>Customers</span>
            <span css={tabStyles}>Segments</span>
          </div>
          <div css={filtersStyles}>
            <button type="button" css={textButtonStyles}>
              <SearchIcon />
              Search
            </button>
            <button type="button" css={textButtonStyles}>
              Sort
              <ChevronDownIcon />
            </button>
            <div
              css={css`
                position: relative;
              `}
            >
              <button
                ref={filterBtnRef}
                type="button"
                css={textButtonStyles}
                onClick={() => {
                  if (hasFilters && menuSource !== 'filter') {
                    filtersEverApplied.current = true;
                    setAppliedFilters([]);
                    setFilterMenuOpen(false);
                    setMenuSource(null);
                  } else {
                    const opening = !filterMenuOpen;
                    if (opening && filterBtnRef.current) {
                      const rect = filterBtnRef.current.getBoundingClientRect();
                      setMenuFixedPos({ top: rect.bottom + 4, left: rect.left });
                    }
                    setFilterMenuOpen(opening);
                    setMenuSource(opening ? 'filter' : null);
                  }
                }}
              >
                {hasFilters && menuSource !== 'filter' ? 'Clear filters' : 'Filter'}
              </button>
            </div>
            <button type="button" css={iconButtonStyles}>
              <EllipsisIcon />
            </button>
            <button type="button" css={addButtonStyles}>
              <span
                css={css`
                  color: #9e9e95;
                  display: flex;
                `}
              >
                <PlusIcon />
              </span>
              Add subscriber
            </button>
          </div>
        </div>

        {hasFilters && (
          <div css={appliedFiltersRowStyles}>
            {appliedFilters.map(filter => {
              const panelConfig = filterSubPanels[filter.category];
              const isEditingCondition =
                editingChip?.id === filter.id && editingChip.section === 'condition';
              const isEditingValues =
                editingChip?.id === filter.id && editingChip.section === 'values';
              const isEditing = isEditingCondition || isEditingValues;

              return (
                <div
                  key={filter.id}
                  css={[
                    filterChipStyles,
                    css`
                      position: relative;
                    `,
                  ]}
                  ref={isEditing ? editingChipRef : undefined}
                >
                  <span css={filterChipLabelStyles}>{filter.category}</span>
                  {filter.condition && (
                    <div
                      css={css`
                        display: contents;
                      `}
                    >
                      <span css={filterChipDividerStyles} />
                      <span
                        role="button"
                        tabIndex={0}
                        css={[
                          filterChipConditionStyles,
                          filterChipClickableStyles,
                          css`
                            position: relative;
                          `,
                        ]}
                        onClick={() =>
                          setEditingChip(
                            isEditingCondition ? null : { id: filter.id, section: 'condition' },
                          )
                        }
                        onKeyDown={e => {
                          if (e.key === 'Enter')
                            setEditingChip(
                              isEditingCondition ? null : { id: filter.id, section: 'condition' },
                            );
                        }}
                      >
                        {filter.condition}
                        {isEditingCondition && panelConfig?.conditions && (
                          <div
                            css={[
                              chipDropdownStyles,
                              css`
                                left: calc(-1 * var(--grn-space-s2) + 10px);
                              `,
                            ]}
                          >
                            <div css={filterGroupStyles}>
                              {panelConfig.conditions.map(c => (
                                <button
                                  type="button"
                                  key={c.label}
                                  css={conditionItemStyles}
                                  onClick={e => {
                                    e.stopPropagation();
                                    updateFilter(filter.id, { condition: c.label });
                                    setEditingChip(null);
                                  }}
                                >
                                  <span css={filterItemLabelStyles}>{c.label}</span>
                                  {c.label === filter.condition && (
                                    <span css={conditionCheckStyles}>
                                      <CheckIcon />
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </span>
                    </div>
                  )}
                  <span css={filterChipDividerStyles} />
                  <span
                    role="button"
                    tabIndex={0}
                    css={[
                      filterChipValueStyles,
                      filterChipClickableStyles,
                      css`
                        position: relative;
                      `,
                    ]}
                    onClick={() =>
                      setEditingChip(isEditingValues ? null : { id: filter.id, section: 'values' })
                    }
                    onKeyDown={e => {
                      if (e.key === 'Enter')
                        setEditingChip(
                          isEditingValues ? null : { id: filter.id, section: 'values' },
                        );
                    }}
                  >
                    <span
                      css={css`
                        display: block;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        min-width: 0;
                        flex: 1;
                      `}
                    >
                      {filter.values.join(', ')}
                    </span>
                    {isEditingValues && panelConfig && (
                      <ChipValuesDropdown
                        filter={filter}
                        config={panelConfig}
                        onUpdate={values => {
                          if (values.length === 0) {
                            removeFilter(filter.id);
                          } else {
                            updateFilter(filter.id, { values });
                          }
                        }}
                        onClose={() => setEditingChip(null)}
                      />
                    )}
                  </span>
                  <span css={filterChipDividerStyles} />
                  <button
                    type="button"
                    aria-label="Remove filter"
                    css={filterChipCloseStyles}
                    onClick={() => removeFilter(filter.id)}
                  >
                    <CrossIcon />
                  </button>
                </div>
              );
            })}
            <div
              css={css`
                position: relative;
              `}
            >
              <button
                ref={plusBtnRef}
                type="button"
                aria-label="Add filter"
                css={addFilterIconButtonStyles}
                onClick={() => {
                  if (!filterMenuOpen && plusBtnRef.current) {
                    const rect = plusBtnRef.current.getBoundingClientRect();
                    setMenuFixedPos({ top: rect.bottom + 4, left: rect.left });
                  }
                  const opening = !filterMenuOpen;
                  setFilterMenuOpen(opening);
                  setMenuSource(opening ? 'plus' : null);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M7 1V13M1 7H13"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Single filter dropdown menu — renders with fixed positioning */}
        <div
          ref={filterRef}
          css={[
            filterDropdownStyles,
            css`
              position: fixed;
              top: ${menuFixedPos?.top ?? -9999}px;
              left: ${menuFixedPos?.left ?? -9999}px;
              right: auto;
            `,
            filterMenuOpen ? filterDropdownVisibleStyles : filterDropdownHiddenStyles,
          ]}
          onMouseMove={safeTriangle.trackMouse}
          onMouseLeave={() => {
            safeTriangle.cleanup();
            setHoveredFilter(null);
            safeTriangle.setActive(null);
          }}
        >
          {filterGroups.map((group, gi) => (
            <React.Fragment key={group.join('-')}>
              {gi > 0 && <div css={filterDividerStyles} />}
              <div css={filterGroupStyles}>
                {group.map(item => (
                  <div
                    key={item}
                    css={css`
                      position: relative;
                      z-index: ${hoveredFilter === item ? 10 : 0};
                    `}
                    onMouseEnter={() => {
                      safeTriangle.requestHover(item, val => {
                        setHoveredFilter(val);
                        safeTriangle.setActive(val);
                      });
                    }}
                  >
                    <button
                      type="button"
                      css={[filterItemStyles, hoveredFilter === item && filterItemActiveStyles]}
                    >
                      <span css={filterItemLabelStyles}>{item}</span>
                      <span css={filterItemChevronStyles}>
                        <ChevronRightIcon />
                      </span>
                    </button>
                    {filterSubPanels[item] && (
                      <div
                        ref={hoveredFilter === item ? safeTriangle.submenuRef : undefined}
                        css={css`
                          position: absolute;
                          left: calc(100% + 4px);
                          top: 0;
                          z-index: 30;
                        `}
                        onMouseEnter={() => {
                          safeTriangle.cleanup();
                        }}
                      >
                        <FilterSubPanel
                          config={filterSubPanels[item]}
                          visible={hoveredFilter === item}
                          category={item}
                          onApplyFilter={handleApplyFilter}
                          appliedFilters={appliedFilters}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>

        {isLoading ? (
          <div css={loadingContainerStyles}>
            <div css={spinnerStyles} />
          </div>
        ) : (
          <table
            css={[
              tableStyles,
              !hasFilters &&
                css`
                  margin-top: 52px;
                `,
            ]}
          >
            <thead>
              <tr>
                <th css={[thStyles, thSubscriberStyles]} onClick={() => handleSort('email')}>
                  <div css={thSortableStyles}>
                    Subscriber
                    <SortArrow column="email" sortKey={sortKey} sortDir={sortDir} />
                  </div>
                </th>
                <th css={[thStyles, thStatusStyles]} onClick={() => handleSort('status')}>
                  <div css={thSortableStyles}>
                    Status
                    <SortArrow column="status" sortKey={sortKey} sortDir={sortDir} />
                  </div>
                </th>
                <th css={[thStyles, thActivityStyles]} onClick={() => handleSort('lastActivity')}>
                  <div css={thSortableStyles}>
                    Last activity
                    <SortArrow column="lastActivity" sortKey={sortKey} sortDir={sortDir} />
                  </div>
                </th>
                <th css={[thStyles, thSalesStyles]} onClick={() => handleSort('totalSales')}>
                  <div css={thSortableStyles}>
                    Total sales
                    <SortArrow column="totalSales" sortKey={sortKey} sortDir={sortDir} />
                  </div>
                </th>
                <th css={[thStyles, thSegmentsStyles]}>Segments</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSubscribers.map(sub => (
                <tr key={sub.email} css={rowStyles}>
                  <td css={cellStyles}>
                    <span css={emailStyles}>{sub.email}</span>
                    <span css={nameStyles}>{sub.name}</span>
                  </td>
                  <td css={cellStyles}>
                    <span
                      css={[statusBadgeStyles, sub.status === 'Active' && statusBadgeActiveStyles]}
                    >
                      {sub.status}
                    </span>
                  </td>
                  <td css={cellStyles}>
                    {sub.lastActivity === 'Resend opt-in' ? (
                      <span css={resendLinkStyles}>{sub.lastActivity}</span>
                    ) : (
                      <span css={activityStyles}>{formatLastActivity(sub.lastActivity)}</span>
                    )}
                  </td>
                  <td css={cellStyles}>
                    <span css={salesStyles}>{sub.totalSales}</span>
                  </td>
                  <td css={segmentsCellStyles}>
                    <SegmentsCell segments={sub.segments} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!isLoading && totalPages > 1 && (
          <div css={paginationStyles}>
            <button
              type="button"
              css={paginationButtonStyles}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <ChevronLeftIcon />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                type="button"
                key={page}
                css={[paginationButtonStyles, currentPage === page && paginationActiveStyles]}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              css={paginationButtonStyles}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
